
import { supabase } from '@/lib/customSupabaseClient';
import { parseValue } from './utils';

const normalizeString = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
};

const removeLeadingZeros = (value) => {
    if (!value) return '';
    return String(value).trim().replace(/^0+/, '');
};

const findOrCreate = async (name, collection, addFunction, type, user, openAliasModal, preloadedAliases = [], context = {}) => {
    if (!name || typeof name !== 'string') {
        return { matchedItem: null, wasCreated: false };
    }
    const normalizedName = normalizeString(name);

    // 1. Check local collection first
    let matchedItem = collection.find(item => normalizeString(item.name) === normalizedName);
    if (matchedItem) {
        return { matchedItem, wasCreated: false };
    }

    // 2. Check preloaded aliases cache
    const alias = preloadedAliases.find(a => a.type === type && normalizeString(a.alias_text) === normalizedName);
    
    if (alias) {
        matchedItem = collection.find(item => normalizeString(item.name) === normalizeString(alias.canonical_name));
        if (matchedItem) return { matchedItem, wasCreated: false };
    } else {
        // Fallback for DB just in case it wasn't preloaded correctly
        const { data: dbAlias, error: aliasError } = await supabase
            .from('aliases')
            .select('canonical_name')
            .eq('user_id', context.activeAccountId || user.id)
            .eq('alias_text', name)
            .eq('type', type)
            .maybeSingle();

        if (aliasError) console.error("Error fetching alias:", aliasError);

        if (dbAlias) {
            matchedItem = collection.find(item => normalizeString(item.name) === normalizeString(dbAlias.canonical_name));
            if (matchedItem) {
                preloadedAliases.push({
                    type: type,
                    alias_text: name,
                    canonical_name: dbAlias.canonical_name
                });
                return { matchedItem, wasCreated: false };
            }
        }
    }

    // 3. Not found anywhere, prompt user
    const newName = await openAliasModal(name, type, addFunction);
    if (!newName) {
        throw new Error(`"${name}" não é um(a) ${type === 'flag' ? 'bandeira' : 'forma de pagamento'} válido(a).`);
    }

    matchedItem = collection.find(item => item.name === newName);
    if (!matchedItem) {
        const newItem = await addFunction({ name: newName });
        if (!newItem) {
            throw new Error(`Ocorreu um erro ao criar o novo item.`);
        }
        matchedItem = newItem;
        // IMPORTANT: Mutate the collection array so the next loop iteration finds it!
        collection.push(matchedItem);
    }

    const { error: newAliasError } = await supabase
        .from('aliases')
        .insert({ user_id: context.activeAccountId, alias_text: name, canonical_name: newName, type });

    if (newAliasError) console.error("Error creating alias:", newAliasError);

    // Add to local cache for next iteration
    preloadedAliases.push({
        type: type,
        alias_text: name,
        canonical_name: newName
    });

    return { matchedItem, wasCreated: false };
};

const parseDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;

    // Se já está no formato ISO (YYYY-MM-DD), retorna diretamente
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
        return dateString.split('T')[0];
    }

    const parts = dateString.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/);
    if (!parts) {
        const isoDate = new Date(dateString);
        return isNaN(isoDate) ? null : isoDate.toISOString().split('T')[0];
    }

    // Interpretar como formato brasileiro: DD/MM/AAAA
    const day = parseInt(parts[1], 10);
    const month = parseInt(parts[2], 10);
    let year = parseInt(parts[3], 10);

    if (year < 100) {
        year += 2000;
    }

    // Validar se é uma data válida
    if (day > 0 && day <= 31 && month > 0 && month <= 12 && year > 1900) {
        return new Date(year, month - 1, day).toISOString().split('T')[0];
    }

    return null;
};

// Função checkDuplicateSale removida - sistema agora permite vendas duplicadas

// Função checkDuplicateReceipt removida - sistema agora permite recebimentos duplicados para modelo 1:1

export const processSaleData = async (sale, context, isEdit = false) => {
    const processed = {};

    const dataVenda = isEdit ? sale.data : sale.data;
    const dataPgt = isEdit ? sale.dataPgt : null;
    const valorCompraRaw = sale.valorCompra || sale.valorcompra || '0';
    const valorRecebidoRaw = sale.valorRecebido || '0';
    const tipoTransacaoRaw = sale.tipoTransacao || sale.tipotransacao || '';
    const parcelasRaw = sale.parcelas || '1';
    const bandeiraRaw = sale.bandeira || '';
    const empresaRaw = sale.empresa || '';
    const codigoAutorizacaoRaw = sale.codigoAutorizacao || sale.codigoautorizacao || '';
    const codigoPedidoRaw = sale.codigoPedido || sale.codigopedido || '';
    const statusRaw = sale.status || 'Em Aberto';

    processed.data = parseDate(dataVenda);
    if (!processed.data) throw new Error(`Data da venda inválida: "${dataVenda}"`);
    if (dataPgt) processed.datapgt = parseDate(dataPgt);

    processed.valorcompra = parseValue(valorCompraRaw);
    if (isNaN(processed.valorcompra)) throw new Error(`Valor da compra inválido: "${valorCompraRaw}"`);

    processed.valorrecebido = parseValue(valorRecebidoRaw);
    if (isNaN(processed.valorrecebido)) processed.valorrecebido = 0;

    // Preservar formato original das parcelas para modelo 1:1
    processed.parcelas = parcelasRaw || '1';

    // Manter tipo de transação como inserido, sem transformações baseadas em parcelas
    let finalTipoTransacao = tipoTransacaoRaw;

    const { matchedItem: matchedMethod } = await findOrCreate(finalTipoTransacao, context.paymentMethods, context.handleAddPaymentMethod, 'payment_method', context.user, context.openAliasModal, context.preloadedAliases, context);
    if (!matchedMethod) throw new Error(`Forma de pagamento inválida: "${finalTipoTransacao}"`);
    processed.tipotransacao = matchedMethod.name;

    const { matchedItem: matchedFlag } = await findOrCreate(bandeiraRaw, context.flags, context.handleAddFlag, 'flag', context.user, context.openAliasModal, context.preloadedAliases, context);
    if (!matchedFlag) throw new Error(`Bandeira inválida: "${bandeiraRaw}"`);
    processed.bandeira = matchedFlag.name;

    let operatorName;
    if (isEdit) {
        const operator = context.operators.find(op => op.name === empresaRaw);
        if (!operator) throw new Error(`Operadora inválida: "${empresaRaw}"`);
        operatorName = operator.name;
    } else {
        const operator = context.operators.find(op => op.id === context.operatorId);
        if (!operator) throw new Error(`Operadora não encontrada`);
        operatorName = operator.name;
    }
    processed.empresa = operatorName;

    processed.codigoautorizacao = removeLeadingZeros(codigoAutorizacaoRaw);
    processed.codigopedido = removeLeadingZeros(codigoPedidoRaw);
    processed.status = statusRaw;
    processed.user_id = context.activeAccountId;

    // Verificação de duplicatas removida - sistema agora permite vendas duplicadas

    return processed;
};

export const processReceiptData = async (receipt, context, isEdit = false) => {
    const processed = {};

    const dataPagamentoRaw = isEdit ? receipt.data_pagamento : receipt.dataPagamento;
    const valorRaw = isEdit ? receipt.valor : receipt.valor;
    const bandeiraRaw = isEdit ? receipt.bandeira : receipt.bandeira;
    const tipoRaw = isEdit ? receipt.tipo : receipt.tipo;
    const parcelasRaw = isEdit ? receipt.parcelas : receipt.parcelas;
    const codigoAutorizacaoRaw = isEdit ? receipt.codigo_autorizacao : receipt.codigoAutorizacao;
    const codigoPedidoRaw = isEdit ? receipt.codigopedido : receipt.codigoPedido;
    const operatorIdRaw = isEdit ? receipt.operator_id : context.operatorId;

    processed.data_pagamento = parseDate(dataPagamentoRaw);
    if (!processed.data_pagamento) throw new Error(`Data de pagamento inválida: "${dataPagamentoRaw}"`);

    processed.valor = parseValue(valorRaw);
    if (isNaN(processed.valor)) throw new Error(`Valor inválido: "${valorRaw}"`);

    const { matchedItem: matchedFlag } = await findOrCreate(bandeiraRaw, context.flags, context.handleAddFlag, 'flag', context.user, context.openAliasModal, context.preloadedAliases, context);
    if (!matchedFlag) throw new Error(`Bandeira inválida: "${bandeiraRaw}"`);
    processed.bandeira = matchedFlag.name;

    // Preservar formato original das parcelas para modelo 1:1
    processed.parcelas = parcelasRaw || '1';

    // Manter tipo como inserido, sem transformações baseadas em parcelas
    let finalTipo = tipoRaw;

    const { matchedItem: matchedMethod } = await findOrCreate(finalTipo, context.paymentMethods, context.handleAddPaymentMethod, 'payment_method', context.user, context.openAliasModal, context.preloadedAliases, context);
    if (!matchedMethod) throw new Error(`Tipo de pagamento inválido: "${finalTipo}"`);
    processed.tipo = matchedMethod.name;

    processed.codigo_autorizacao = removeLeadingZeros(codigoAutorizacaoRaw);
    processed.codigopedido = removeLeadingZeros(codigoPedidoRaw);
    processed.operator_id = Number(operatorIdRaw);
    if (!processed.operator_id) throw new Error("Operadora não selecionada.");
    processed.user_id = context.activeAccountId;

    return processed;
};
